import type { Memo, MemoBlock, MemoEntry, MemoUser, SectionHistoryEntry, UUID } from "@/lib/types";
import { buildMemoSections } from "@/lib/memoSections";
import { byPositionThenId } from "@/lib/positionOrder";
import { historyToEntry, mergeSectionTimeline } from "@/lib/memoSectionTimeline";
import { formatMemoDate, isoWeekNumber, formatClockTime, formatClosureDate } from "@/lib/format";

/**
 * A pure, static model of a Protokoll ready for print. `buildPrintDoc` flattens a
 * live `Memo` (plus a people lookup and the fetched cross-week section history)
 * into this tree; React print components render it without any further fetching,
 * ordering, or formatting logic. No React, no I/O — deterministic from its inputs.
 */
export interface PrintTodo {
  done: boolean;
  assigneeName: string | null;
  dueDate: string | null;
}

export interface PrintLine {
  id: string;
  kind: "point" | "update";
  /** 0 = section-level point, 1 = update welded under a point. */
  indent: 0 | 1;
  bodyMarkdown: string;
  /** Present only when the point carries an assignee and/or a due date. */
  todo: PrintTodo | null;
  /** True when the point is retired/abgeschlossen — independent of whether a
   * closure date exists (historical/merged entries can be retired with a null
   * retired_at). Drives the ✓ + strike; `closedDate` only adds the parenthetical. */
  closed: boolean;
  /** Formatted closure date for an abgeschlossen point, else null. */
  closedDate: string | null;
  /** Carried in from an earlier meeting (read-only, dated). */
  historical: boolean;
  /** Formatted date shown on updates / historical lines. */
  logDate: string | null;
}

export interface PrintSection {
  anchorId: string;
  /** Zwischenüberschrift text, or null for a block's leading run. */
  headingText: string | null;
  headingRetired: boolean;
  headingClosedDate: string | null;
  lines: PrintLine[];
}

export interface PrintBlock {
  id: string;
  number: number;
  title: string;
  sections: PrintSection[];
}

export interface PrintDoc {
  title: string;
  /** formatMemoDate(meeting_date). */
  dateLabel: string;
  /** `KW ${isoWeekNumber(meeting_date)}`. */
  weekLabel: string;
  /** "09:00–10:00" | "ab 09:00" | null (both null). */
  timeLabel: string | null;
  /** Display names, deduped, in participants order. */
  participantNames: string[];
  participantCount: number;
  /** status !== "finalized". */
  isDraft: boolean;
  blocks: PrintBlock[];
}

function toPrintLine(e: MemoEntry, people: Map<UUID, MemoUser>): PrintLine {
  const hasTodo = Boolean(e.assignee_user_id) || Boolean(e.due_date);
  const assigneeName = e.assignee_user_id
    ? (people.get(e.assignee_user_id)?.display_name ?? null)
    : null;
  const historical = Boolean(e.is_historical) || Boolean(e.carried_from_entry_id);
  return {
    id: e.id,
    kind: e.kind === "update" ? "update" : "point",
    indent: e.kind === "update" ? 1 : 0,
    bodyMarkdown: e.body,
    todo: hasTodo
      ? {
          done: e.task_done,
          assigneeName,
          dueDate: e.due_date ? formatClosureDate(e.due_date) : null,
        }
      : null,
    // "Is this closed" is a boolean on `retired`, not on the date: a retired point
    // with a null retired_at still gets its ✓/strike; `closedDate` is null then and
    // the "(abgeschlossen …)" parenthetical is simply omitted.
    closed: e.retired,
    closedDate: e.retired_at ? formatClosureDate(e.retired_at) : null,
    historical,
    // Dates belong only to updates and carried-in historical lines; a fresh point
    // must not print a trailing muted date even if it happens to carry a log_date.
    logDate:
      e.kind === "update" || historical ? (e.log_date ? formatMemoDate(e.log_date) : null) : null,
  };
}

function timeLabel(start: string | null, end: string | null): string | null {
  if (!start && !end) return null;
  if (start && end) return `${formatClockTime(start)}–${formatClockTime(end)}`;
  if (start) return `ab ${formatClockTime(start)}`;
  return null;
}

// Dedupe by identity (participant.id), NOT display name — the same as the screen
// code — so two distinct people who happen to share a display name both survive and
// the count is right. Keeps first-occurrence order.
function participantNames(participants: MemoUser[]): string[] {
  const seen = new Set<string>();
  const names: string[] = [];
  for (const p of participants) {
    if (seen.has(p.id)) continue;
    seen.add(p.id);
    names.push(p.display_name ?? p.username ?? "?");
  }
  return names;
}

function buildSectionLines(
  block: MemoBlock,
  section: ReturnType<typeof buildMemoSections>[number],
  historyByAnchor: Map<string, SectionHistoryEntry[]>,
  people: Map<UUID, MemoUser>,
): PrintLine[] {
  const history = historyByAnchor.get(section.anchorId);
  const timeline =
    history && history.length > 0
      ? mergeSectionTimeline(
          section.entries,
          history.map((h) => historyToEntry(h, block.id)),
        )
      : section.entries;
  return timeline.map((e) => toPrintLine(e, people));
}

export function buildPrintDoc(
  memo: Memo,
  people: Map<UUID, MemoUser>,
  historyByAnchor: Map<string, SectionHistoryEntry[]>,
): PrintDoc {
  const orderedBlocks = [...memo.blocks].sort(byPositionThenId);

  const blocks: PrintBlock[] = orderedBlocks.map((block, i) => ({
    id: block.id,
    number: i + 1,
    title: block.title,
    sections: buildMemoSections(block).map((section) => ({
      anchorId: section.anchorId,
      headingText: section.heading?.body ?? null,
      headingRetired: Boolean(section.heading?.retired),
      headingClosedDate: section.heading?.retired
        ? formatClosureDate(section.heading.retired_at)
        : null,
      lines: buildSectionLines(block, section, historyByAnchor, people),
    })),
  }));

  const names = participantNames(memo.participants);

  return {
    title: memo.title,
    dateLabel: formatMemoDate(memo.meeting_date),
    weekLabel: `KW ${isoWeekNumber(memo.meeting_date)}`,
    timeLabel: timeLabel(memo.start_time, memo.end_time),
    participantNames: names,
    participantCount: names.length,
    isDraft: memo.status !== "finalized",
    blocks,
  };
}
